import { useState } from "react"
import { ChevronsUpDown } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"

interface Option<T> {
  value: string
  label: string
  data?: T
}

export type FilterSelectProps<T> = {
  label?: string
  placeholder?: string
  options: Option<T>[]
  align?: "start" | "center" | "end"
  renderOption?: (option: Option<T>) => React.ReactNode
  onSelect?: (value: string) => void
}

const FilterSelect = <T,>({
  label,
  placeholder = "Type to search...",
  options,
  align = "center",
  renderOption,
  onSelect,
}: FilterSelectProps<T>) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {label}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-auto max-w-[500px]" align={align}>
        <Command>
          <CommandInput
            placeholder={t("common.placeholder.search") || placeholder}
          />
          <CommandList>
            <CommandEmpty>{t("common.message.noResultsFound")}</CommandEmpty>
            <CommandGroup>
              {options.map((option, index) => (
                <CommandItem
                  key={index}
                  value={option.value}
                  onSelect={(currentValue) => {
                    setOpen(false)
                    onSelect?.(currentValue)
                  }}
                >
                  {renderOption?.(option) || option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default FilterSelect
